
import { NgStyle } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { CustomToolKitTypeArr, isToolConfigEnumParam, isToolConfigParam, ToolConfigCustomToolKit } from '@shared/index';
import { removeFromArray } from "@reflact/tsutil";
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from "primeng/inputtext";
import type { Menu } from 'primeng/menu';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { BlocklyStorageService } from 'src/app/shared/services/blockly-storage.service';
import { BotService } from 'src/app/shared/services/bot.service';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { SkillService } from 'src/app/shared/services/skill.service';
import { InPlaceValidatorComponent } from "../../../../../../shared/components/ai-bot/utils/in-place-validator/in-place-validator.component";
import { SkillIdInputComponent } from "../../../../../../shared/components/ai-bot/utils/skill-id-input/skill-id-input.component";
import { SkillUsageListComponent } from "../../../../../../shared/components/ai-bot/utils/skill-usage-list/skill-usage-list.component";
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { IfthenTabDetailComponent } from './ifthen-tab-detail/ifthen-tab-detail.component';
import { DrawerModule } from 'primeng/drawer';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type VarsHeader = { botId: string, botColor: string, botName: string, botVoice: string, botIcon: string };
@Component({
  imports: [NgStyle, InPlaceValidatorComponent,
    FormsModule,
    NgSelectModule,
    SkillIdInputComponent,
    SkillUsageListComponent,
    ManualLinkComponent,
    MenuModule,
    ButtonModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    TableModule,
    BulkActionBarComponent,
    MessageModule,
    InputText,
    TooltipModule,
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    IfthenTabDetailComponent,
    DrawerModule
  ],
  templateUrl: './ifthen-tab.component.html',
  styleUrl: './ifthen-tab.component.scss',
  providers: [ConfirmationService]
})
export class IfthenTabComponent implements OnInit {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-delete',
        label: $localize`:@@bulk-delete:Löschen`,
        icon: 'ri ri-delete-bin-line',
        command: () => this.removeSelectedCustomToolKits()
      }
    ];
  }

  protected customToolKitName: string = '';
  protected customToolKitDescription: string = '';
  protected customToolKitContentTypes = CustomToolKitTypeArr;
  protected isToolConfigEnumParam = isToolConfigEnumParam;
  protected isToolConfigParam = isToolConfigParam;
  protected isToolkitAddingInProgress: boolean = false;
  public requiredValidator: ValidatorFn[] = [Validators.required];
  public selectedCustomToolKits: ToolConfigCustomToolKit[] = [];

  public get allSelected(): boolean {
    return this.customToolKits.length > 0 && this.selectedCustomToolKits.length === this.customToolKits.length;
  }

  constructor(
    public router: Router,
    public rss: RouteShareService,
    public skillService: SkillService,
    private editorItemService: EditorItemsService,
    private blocklyStorageService: BlocklyStorageService,
    private botService: BotService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) { }

  public ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['open']) {
        this.showDetailDrawer(params['open'] as string);
      }
    });
  }

  public get customToolKits(): ToolConfigCustomToolKit[] {
    return this.rss.botConfig.toolConfigs.filter((tc) => tc.tool === 'customtoolkit') as ToolConfigCustomToolKit[];
  }

  protected checkForAdding(): boolean {
    if (this.customToolKitDescription.trim() === '') return true;
    return this.isToolkitAddingInProgress;
  }

  protected async addCustomToolKit(): Promise<void> {
    this.isToolkitAddingInProgress = true;
    const newCustomToolKit: ToolConfigCustomToolKit = {
      skillId: '',
      tool: 'customtoolkit',
      name: this.customToolKitName.trim(),
      description: this.customToolKitDescription.trim(),
      enabled: true,
      params: [],
      id: '',
      type: 'js',
      content: '',
      toolCallAnswer: [''],
      answerMode: 'sequence',
      detectedErrors: false
    };
    newCustomToolKit.name = await this.botService.suggestName(this.rss.botConfig._id, newCustomToolKit.description);
    newCustomToolKit.skillId = await this.botService.suggestSkill(this.rss.botConfig._id, newCustomToolKit.description, newCustomToolKit.name);
    newCustomToolKit.name = this.uniquify(newCustomToolKit.name, new Set(this.customToolKits.map(t => t.name).filter(Boolean)), 30, '_');
    newCustomToolKit.skillId = this.uniquify(newCustomToolKit.skillId, new Set(this.customToolKits.map(t => t.skillId).filter(Boolean)), 15, '');
    const existingIds = new Set(this.customToolKits.map(t => t.id));
    this.rss.botConfig.toolConfigs.unshift(newCustomToolKit);
    await this.rss.saveBotConfigField('toolConfigs');
    this.customToolKitName = '';
    this.customToolKitDescription = '';
    this.isToolkitAddingInProgress = false;
    const savedTool = this.customToolKits.find(t => !existingIds.has(t.id));
    if (savedTool) {
      this.showDetailDrawer(savedTool.id);
    }
  }

  public async copyCustomToolKit(id: string): Promise<void> {
    await this.rss.copyIfThen(id);
    await this.blocklyStorageService.loadBlocklyStages();
    this.editorItemService.loadEditorItems();
  }

  protected removeCustomToolKit(id: string): void {
    removeFromArray(this.rss.botConfig.toolConfigs, tc => tc.id === id);
    this.selectedCustomToolKits = this.selectedCustomToolKits.filter(tool => tool.id !== id);
    void this.rss.saveBotConfigField('toolConfigs');
  }

  public toggleAll(checked: boolean): void {
    this.selectedCustomToolKits = checked ? [...this.customToolKits] : [];
  }

  public removeSelectedCustomToolKits(): void {
    this.confirmBulkDelete(async () => {
      const selectedIds = new Set(this.selectedCustomToolKits.map(tool => tool.id));
      this.rss.botConfig.toolConfigs = this.rss.botConfig.toolConfigs.filter(tool => !selectedIds.has(tool.id));
      this.selectedCustomToolKits = [];
      await this.rss.saveBotConfigField('toolConfigs');
    });
  }

  private confirmBulkDelete(accept: () => Promise<void>): void {
    this.confirmationService.confirm({
      header: $localize`:@@bulk-delete-skills-header:Ausgewählte Einträge löschen?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-delete-skills-message:Möchtest du die ausgewählten Einträge wirklich löschen?`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete:Löschen`,
        severity: 'danger'
      },
      accept
    });
  }

  public customToolKitContextMenu: MenuItem[] = [];

  public openCustomToolKitMenu(event: Event, menu: Menu, custom: ToolConfigCustomToolKit) {
    this.customToolKitContextMenu = this.buildCustomToolKitMenuItems(custom);
    menu.toggle(event);
  }

  private buildCustomToolKitMenuItems(custom: ToolConfigCustomToolKit): MenuItem[] {
    return [
      {
        label: $localize`:@@edit:Bearbeiten`,
        icon: 'ri ri-pencil-fill',
        command: () => this.showDetailDrawer(custom.id)
      },
      {
        label: $localize`:@@copy:Duplizieren`,
        icon: 'ri ri-file-copy-fill',
        command: () => void this.copyCustomToolKit(custom.id)
      },
      {
        label: $localize`:@@delete:Löschen`,
        icon: 'ri ri-close-large-fill',
        command: () => this.removeCustomToolKit(custom.id)
      }
    ];
  }

  public selectedToolId: string = '';
  public detailDrawer: boolean = false;

  public showDetailDrawer(id: string): void {
    this.selectedToolId = id;
    this.detailDrawer = true;
  }

  private uniquify(value: string, taken: Set<string>, maxLen: number, sep: string): string {
    for (let i = 2; taken.has(value) && i < 100; i++) {
      const suffix = sep + i;
      value = value.slice(0, maxLen - suffix.length) + suffix;
    }
    return value;
  }
}
